import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { SITE } from "@/site/content";

const scrollToDemo = () => {
  const el = document.querySelector("#demo");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

/**
 * Mobile-only sticky bottom CTA bar.
 * Appears after user scrolls past the hero (~600px) and disappears
 * once the lead-capture form is in view.
 */
export const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let demoInView = false;

    const onScroll = () => {
      setVisible(window.scrollY > 600 && !demoInView);
    };

    const demoEl = document.querySelector("#demo");
    let observer;
    if (demoEl && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          demoInView = entries[0].isIntersecting;
          onScroll();
        },
        { threshold: 0.15 }
      );
      observer.observe(demoEl);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="lg:hidden fixed inset-x-0 bottom-0 z-40 px-3 pb-3"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
          data-testid="sticky-mobile-cta"
        >
          <div className="mx-auto max-w-md rounded-xl bg-gray-900 text-white shadow-2xl border border-white/10 backdrop-blur-xl flex items-stretch overflow-hidden">
            <button
              onClick={scrollToDemo}
              data-testid="sticky-cta-primary"
              className="flex-1 flex items-center justify-between gap-3 px-4 py-3 text-left active:bg-gray-800"
            >
              <span className="flex flex-col leading-tight">
                <span className="font-display font-bold text-sm">
                  Get a free demo
                </span>
                <span className="text-[11px] text-blue-300">
                  Yours in 2–3 business days · no payment
                </span>
              </span>
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-blue-700">
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
            <a
              href={`tel:${SITE.contact.phone.replace(/[^\d+]/g, "")}`}
              data-testid="sticky-cta-call"
              aria-label={`Call ${SITE.contact.phone}`}
              className="flex items-center justify-center w-14 border-l border-white/10 active:bg-gray-800"
            >
              <Phone className="h-5 w-5 text-blue-300" />
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
